import Box from '@mui/material/Box';
import Container, { ContainerProps } from '@mui/material/Container';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AppContext } from '../../contexts/app-context';
import { AuthContext } from '../../contexts/auth-context';
import { useRetryFailedQueries } from '../../hooks/use-retry-failed-queries';
import { useSessionActions } from '../../hooks/use-session-actions';
import { useAgentAccessDenied } from '../alliance/agent-access';
import { AllianceAppBarSpacer } from '../alliance/alliance-app-bar';
import { AppBanners } from '../alliance/app-banners';
import { LoadFailedScreen, NoAgentScreen } from '../alliance/system-screens';
import { AnimateInProps } from '../animate-in';
import { AppShellBar } from '../app-shell-bar';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

/**
 * Wraps every route body. The App Bar lives once in the shell
 * (components/dashboard.tsx); this component tells the shell the page title
 * and loading state, sets the document title, and renders the page content,
 * the load error or the error boundary.
 *
 * While `loading`, the content area stays empty and the shell shows the 3px
 * progress bar under the App Bar (after 150ms). `hasError` shows "Couldn't
 * load this page" (offline vs. server trouble) with Try again, which
 * refetches the failed queries. While impersonating, if every request for
 * that agent was refused (403), the page shows "You don't have access to
 * this agent's data" with Stop impersonating.
 *
 * `fullWidth` pages render outside the shell (the Inactive agent's Wallet),
 * so they bring their own App Bar, with no sidebar.
 */
export function RouteContainer(props: {
  children: JSX.Element;
  routeTitle: string;
  withContainer?: boolean;
  maxWidth?: ContainerProps['maxWidth'];
  loading?: boolean;
  hasError?: boolean;
  paddingBottom?: number;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  noanimation?: boolean;
  // Display full width without spacing for left side navigation
  fullWidth?: boolean;
}) {
  // Context
  const { dispatch } = React.useContext(AppContext);
  const { impersonatedAgent } = React.useContext(AuthContext).state;
  // Hooks
  const retryFailed = useRetryFailedQueries();
  const { stopImpersonating } = useSessionActions();
  const agentDenied = useAgentAccessDenied(impersonatedAgent?.AgtNo);

  const loading = !!props.loading;

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
  }, [dispatch]);

  React.useEffect(() => {
    // Show this page's title and loading state in the shell's App Bar
    dispatch({
      type: 'SET_ROUTE',
      payload: { title: props.routeTitle, loading },
    });
  }, [dispatch, props.routeTitle, loading]);

  React.useEffect(() => {
    // Don't leave the loading bar running after leaving the page
    return () =>
      dispatch({ type: 'SET_ROUTE', payload: { title: '', loading: false } });
  }, [dispatch]);

  let content = props.children;
  if (props.withContainer) {
    content = <Container maxWidth={props.maxWidth}>{props.children}</Container>;
  }

  let routeContent: JSX.Element | null = null;
  if (impersonatedAgent && agentDenied) {
    // Every request for the impersonated agent came back 403
    routeContent = (
      <NoAgentScreen
        agent={{
          name:
            [impersonatedAgent.Fname, impersonatedAgent.Lname]
              .filter(Boolean)
              .join(' ') ||
            impersonatedAgent.AgtName ||
            '',
          id: impersonatedAgent.AgtNo || '',
        }}
        onStopImpersonating={stopImpersonating}
      />
    );
  } else if (loading) {
    routeContent = null;
  } else if (props.hasError) {
    // The page's data didn't load: offline vs. server trouble
    routeContent = <LoadFailedScreen layout="inline" onRetry={retryFailed} />;
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          noanimation={props.noanimation}
          fullWidth={props.fullWidth}
        >
          {content}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Contracting · {props.routeTitle}</title>
      </Helmet>

      {props.fullWidth ? (
        <Box component="main" sx={{ width: '100%' }}>
          <AppShellBar sidebar={false} />
          <AllianceAppBarSpacer />
          <AppBanners />
          {routeContent}
        </Box>
      ) : (
        routeContent
      )}
    </React.Fragment>
  );
}
